// Reusable pagination control. Used by WorkItemsList (top compact form
// in the toolbar + full form at the bottom) and ProjectsList. Renders:
//
//   [« first] [‹ prev]   Page [N]  of M   [next ›] [last »]   25 ▾
//
// `compact` mode hides the page-size dropdown and the count summary, keeping
// just the buttons + page indicator. Useful for the toolbar.
//
// The component is purely controlled — it owns no state. The parent decides
// what to do with onPageChange / onPageSizeChange (typically: clamp + persist).

import { useState, useEffect } from 'react';

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200, 500] as const;

interface PaginationProps {
  page: number;            // 1-based
  totalPages: number;
  pageSize: number;
  total: number;
  pageSizes?: readonly number[];
  onPageChange: (p: number) => void;
  onPageSizeChange?: (n: number) => void;
  compact?: boolean;
}

export function Pagination({
  page,
  totalPages,
  pageSize,
  total,
  pageSizes = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
  compact,
}: PaginationProps) {
  // Local input state so the user can type "12" without each digit firing
  // a re-fetch. Commits on blur or Enter.
  const [pageInput, setPageInput] = useState(String(page));
  useEffect(() => { setPageInput(String(page)); }, [page]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const commitPage = () => {
    const n = Number.parseInt(pageInput, 10);
    if (!Number.isFinite(n)) {
      setPageInput(String(page));
      return;
    }
    const clamped = Math.max(1, Math.min(totalPages, n));
    if (clamped !== page) onPageChange(clamped);
    setPageInput(String(clamped));
  };

  return (
    <div
      className="pagination"
      role="navigation"
      aria-label="Pagination"
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        fontSize: 13,
        color: 'var(--text-secondary)',
      }}
    >
      {!compact && total > 0 && (
        <span style={{ marginRight: 4 }}>
          {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()}
        </span>
      )}

      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => onPageChange(1)}
        disabled={!canPrev}
        aria-label="First page"
        title="First page"
      >
        «
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => onPageChange(page - 1)}
        disabled={!canPrev}
        aria-label="Previous page"
        title="Previous page"
      >
        ‹
      </button>

      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <input
          type="text"
          inputMode="numeric"
          aria-label="Current page"
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={commitPage}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitPage();
            }
          }}
          style={{
            width: 44,
            textAlign: 'center',
            padding: '4px 6px',
            borderRadius: 4,
            border: '1px solid var(--border-strong)',
            background: 'var(--surface-raised)',
            color: 'var(--text-primary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        />
        <span style={{ whiteSpace: 'nowrap' }}>of {totalPages.toLocaleString()}</span>
      </span>

      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => onPageChange(page + 1)}
        disabled={!canNext}
        aria-label="Next page"
        title="Next page"
      >
        ›
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => onPageChange(totalPages)}
        disabled={!canNext}
        aria-label="Last page"
        title="Last page"
      >
        »
      </button>

      {!compact && onPageSizeChange && (
        <select
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          style={{
            marginLeft: 6,
            padding: '4px 6px',
            borderRadius: 4,
            border: '1px solid var(--border-strong)',
            background: 'var(--surface-raised)',
            color: 'var(--text-primary)',
          }}
          title="Rows per page"
        >
          {pageSizes.map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      )}
    </div>
  );
}
